import {IToast} from '@chakra-ui/react'

import LogRocket from 'lib/logrocket'

import useToast from './use-toast'
import useWindowEventListener from './use-window-event-listener'

const toastSettings: IToast = {
  // Require users to manually clear error toasts by setting duration to `null`
  duration: null,
  status: 'error'
}

// Flag to enable error toasts.
const ENABLE_ERROR_TOASTS = true

// Don't show duplicate toasts of the same error.
const toastTracker = new Set<string>() // title + description

export default function useErrorHandlingToast() {
  const toast = useToast()

  function showUniqueToast(title: string, description: string) {
    const slug = title + description
    if (ENABLE_ERROR_TOASTS && !toastTracker.has(slug)) {
      toastTracker.add(slug)
      toast({
        ...toastSettings,
        description,
        title,
        // Remove toast type from tracker
        onCloseComplete: () => toastTracker.delete(slug)
      })
    }
  }

  useWindowEventListener('error', (e) => {
    e.preventDefault()
    LogRocket.captureException(e.error)
    showUniqueToast('Application Error', e.message)
  })

  useWindowEventListener('unhandledrejection', (pre) => {
    pre.preventDefault()
    const e: unknown = pre.reason
    let title = 'Application Exception'
    let description: string = ''
    if (e instanceof Error) {
      // Ignore errors that come from aborting a Fetch. Libraries that handle map tiling will
      // abort fetches when panning across a map faster than the tiles download. Usually, they
      // handle the AbortErrors but there is a bug in safari that causes them to show up anyway.
      // https://github.com/mapbox/mapbox-gl-js/issues/8480
      if (e instanceof DOMException && e.code === e.ABORT_ERR) {
        console.error('Caught AbortError', e)
        return false
      }

      LogRocket.captureException(e)
      description = e.message
      if (e.message === 'Failed to fetch') {
        title = 'Network Error'
        description =
          'Recent changes made may not have been saved. Please make sure you have an active internet connection and reload the page.'
      }
    } else if (e instanceof Response) {
      title = 'Error while communicating with server'
      const value: unknown = (e as any).value
      if (value instanceof Error) {
        description = value.message
      } else if (typeof value === 'string') {
        description = value
      } else {
        description = e.statusText
      }
    } else if (typeof e === 'string') {
      title = e
    }

    showUniqueToast(title, description)
  })
}
